import React from 'react';

import { css, cx } from '@emotion/css';
import { Editor } from '@tiptap/core';
import { BubbleMenu, EditorContent } from '@tiptap/react';

import { useStyles } from '@/hooks/useStyles.ts';
import { Theme } from '@/theme/CreateTheme.ts';
import { CenteredLoader } from '@/uiKit/atoms/pending/CenteredLoader.tsx';
import { RichTextBubbleMenu } from '@/uiKit/molecules/RichText/RichTextBubbleMenu.tsx';
import { RichTextEditionHeader } from '@/uiKit/molecules/RichText/RichTextEditionHeader.tsx';
import { useRichTextEditor } from '@/uiKit/molecules/RichText/RichTextSettings.ts';

export type TextEditorProps = {
  content: string | undefined;
  onChange: (content: string) => void;
  className?: string;
};

export const RichTextEditor: React.FC<TextEditorProps> = ({
  content,
  onChange,
  className,
}) => {
  const styles = useStyles(makeStyles);
  const handleChange = (editor: { editor: Editor }) => {
    onChange(JSON.stringify(editor.editor.getJSON()));
  };
  const editor = useRichTextEditor({
    content: content ? JSON.parse(content) : undefined,
    onUpdate: handleChange,
  });

  if (!editor) {
    return <CenteredLoader />;
  }

  return (
    <div className={cx(styles.container, className)} id="rich-text-editor">
      <RichTextEditionHeader editor={editor} className={styles.header} />
      <div className={styles.editor}>
        <BubbleMenu editor={editor} tippyOptions={{ duration: 100 }}>
          <RichTextBubbleMenu editor={editor} />
        </BubbleMenu>
        <EditorContent
          id="rich-text-editor-content"
          editor={editor}
          className={styles.content}
          content={content}
        />
      </div>
    </div>
  );
};

const makeStyles = (theme: Theme) => ({
  container: css({}),
  header: css({
    position: 'sticky',
    top: theme.spacing(-4),
    right: theme.spacing(-4),
  }),
  editor: css({
    borderColor: theme.palette.divider,
    borderWidth: 1,
    borderStyle: 'solid',
    borderRadius: theme.spacing(4),
    padding: theme.spacing(2),
  }),
  content: css({
    '& .ProseMirror': {
      outline: 'none',
    },
  }),
});
